import React, { useMemo } from 'react';
import { AppBar as CoreAppBar, Box, Fab, Tab, Tabs, Toolbar } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useAppTheme } from '../../themes/useAppTheme';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';

import { UserName } from './UserName';
import { Santa } from './Santa';
import { PAGES } from '../../utils/pages';
import { Logo } from '../common/Logo';
import { Link } from '../common/Link/Link';
import { GlobalLanguageSelector } from '../common/GlobalLanguageSelector/GlobalLanguageSelector';
import { canUserSeeWhatsNew, isTabEnabledByRole } from '../../utils/mockedAppConfig';
import { WhatsNew } from '../WhatsNew/WhatsNew';
import { useAppConfig } from '../../utils/hooks/user';
import { useLocalizationContext } from '../../localization/LocalizationContext';

import { Theme } from '../../themes/types';
import { getEasterTheme } from '../../themes/easterTheme';
import { isGermanChristmasSeason } from '../../utils/seasonalDecor';

const useStyles = makeStyles<Theme>(theme => ({
    appBar: {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: 0,
        flexGrow: 0,
        position: 'sticky',
        top: 0,
        zIndex: 1200,
        overflow: 'visible',
        boxSizing: 'border-box',
        backdropFilter: `blur(${theme.palette.isDarkTheme ? 12 : 8}px)`,
        WebkitBackdropFilter: `blur(${theme.palette.isDarkTheme ? 12 : 8}px)`,
        boxShadow: `inset 0 -1px 0 var(--header-border-color, ${theme.palette.surfaceBorderSubtle})`,
        '&::after': {
            content: '""',
            display: 'var(--header-seasonal-border-display, none)',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 'var(--header-seasonal-line-bottom, -18px)',
            height: 'var(--header-seasonal-line-height, 22px)',
            pointerEvents: 'none',
            backgroundImage: 'url("/assets/easter/easter_line.gif")',
            backgroundRepeat: 'repeat-x',
            backgroundSize: 'auto 100%',
            backgroundPosition: 'left top',
            filter: 'var(--header-seasonal-line-filter, none)',
            opacity: 'var(--header-seasonal-line-opacity, 1)'
        }
    },
    toolbar: {
        minHeight: 72,
        boxSizing: 'border-box',
        justifyContent: 'space-between',
        boxShadow: 'none',
        zIndex: 1100,
        position: 'relative',
        background: 'transparent'
    },
    logoContainer: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        position: 'relative',
        zIndex: 1
    },
    badges: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: '4px',
        position: 'relative',
        paddingRight: '20px',
        zIndex: 3
    },
    germanFlag: {
        display: 'flex',
        flexDirection: 'column',
        width: '6px',
        height: '20px',
        borderRadius: '2px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        '& > div': {
            height: '33.33%',
            width: '100%'
        }
    },
    euFlag: {
        padding: '2px',
        width: '20px',
        height: '20px',
        borderRadius: '2px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        fill: theme.palette.headerFlagEuStars,
        backgroundColor: theme.palette.headerFlagEuBackground
    },
    seasonalMascot: {
        position: 'absolute',
        right: '19px',
        bottom: '-23px',
        height: '37px',
        width: 'auto',
        zIndex: 4,
        pointerEvents: 'none'
    },
    christmasMascot: {
        position: 'absolute',
        right: '15px',
        bottom: '-23px',
        height: '36px',
        width: 'auto',
        zIndex: 4,
        pointerEvents: 'none'
    },
    germanText: {
        fontSize: '9px',
        fontWeight: 600,
        letterSpacing: '0.5px',
        color: theme.palette.headerBadgeText,
        opacity: 0.8,
        textTransform: 'uppercase'
    },
    germanStripeBlack: {
        backgroundColor: theme.palette.headerFlagGermanStripeBlack
    },
    germanStripeRed: {
        backgroundColor: theme.palette.headerFlagGermanStripeRed
    },
    germanStripeGold: {
        backgroundColor: theme.palette.headerFlagGermanStripeGold
    },
    tabs: {
        flex: 1,
        paddingLeft: '20px',
        '& .MuiTabs-indicator': {
            backgroundColor: `var(--header-indicator-color, ${theme.palette.headerTabIndicatorColor})`,
            bottom: 'var(--header-indicator-bottom, 0px)'
        },
        '& .MuiTabs-scrollButtons': {
            color: theme.palette.headerTabTextColor,
            '&:first-of-type': {
                marginLeft: '-30px'
            }
        }
    },
    rightBox: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center'
    },
    logoutButton: {
        width: 'max-content',
        height: 'max-content',
        padding: '0 6px',
        boxShadow: 'none',
        backgroundColor: 'transparent',
        color: theme.palette.headerTabTextColor,
        '&:hover': {
            backgroundColor: theme.palette.headerControlHoverBackground
        }
    },
    linkTabRoot: {
        minHeight: 72,
        minWidth: 0,
        fontSize: 15,
        fontWeight: 600,
        paddingLeft: 16,
        paddingRight: 16,
        color: theme.palette.headerTabTextColor,
        '&.Mui-selected': {
            color: theme.palette.selectedTabTextColor
        }
    },
    linkTabSelected: {
        opacity: 1,
        color: theme.palette.selectedTabTextColor
    }
}));

interface AppBarProps {
    children: React.ReactNode;
}

const AppBar: React.FC<AppBarProps> = ({ children }) => {
    const classes = useStyles();
    const theme = useAppTheme();
    const easterTheme = getEasterTheme(theme.palette.isDarkTheme);
    return (
        <CoreAppBar
            classes={{ root: classes.appBar }}
            position="static"
            style={
                {
                    '--header-seasonal-border-display': easterTheme.enabled ? 'block' : 'none',
                    '--header-border-color': easterTheme.enabled ? easterTheme.headerBorderColor : undefined,
                    '--header-indicator-color': easterTheme.enabled ? easterTheme.headerIndicatorColor : undefined,
                    '--header-indicator-bottom': easterTheme.enabled ? easterTheme.headerIndicatorBottom : undefined,
                    '--header-seasonal-line-bottom': easterTheme.enabled ? easterTheme.headerLine.bottom : undefined,
                    '--header-seasonal-line-height': easterTheme.enabled ? easterTheme.headerLine.height : undefined,
                    '--header-seasonal-line-filter': easterTheme.enabled ? easterTheme.headerLine.filter : undefined,
                    '--header-seasonal-line-opacity': easterTheme.enabled ? easterTheme.headerLine.opacity : undefined
                } as React.CSSProperties
            }
        >
            {children}
            <Santa />
        </CoreAppBar>
    );
};

// Tab Configuration Types
interface TabConfig {
    value: string;
    labelKey: string;
    to: string;
    testId: string;
    condition: boolean;
}

interface HeaderAppConfig {
    dashboardShowI2XMetrics?: boolean;
    callHistory?: boolean;
    reports?: boolean;
    adminKeywordManager?: boolean;
    adminUserManager?: boolean;
    salesTab?: boolean;
    kpiManager?: boolean;
    qualityManagement?: boolean;
    languageSelectorEnabled?: boolean;
}

// Main Component
interface HeaderBaseProps {
    logout: () => void;
    page: string;
    userName?: string | null;
    appConfig: HeaderAppConfig;
    companyId: number;
    locationId: number;
    role: string;
    disableWhatsNew?: boolean;
}

export const HeaderBase: React.FC<HeaderBaseProps> = ({
    logout,
    page,
    userName = null,
    appConfig,
    companyId,
    locationId,
    role,
    disableWhatsNew = false
}) => {
    const classes = useStyles();
    const theme = useAppTheme();
    const localization = useLocalizationContext();
    const salesDashboardOnly = useAppConfig('salesDashboardOnly');
    const easterTheme = getEasterTheme(theme.palette.isDarkTheme);
    const isChristmasThemeEnabled = isGermanChristmasSeason();

    const shouldShowWhatsNew = useMemo(
        () => !disableWhatsNew && canUserSeeWhatsNew({ locationId, role }),
        [disableWhatsNew, locationId, role]
    );

    const tabs: TabConfig[] = useMemo(
        () => [
            {
                value: PAGES.DASHBOARD,
                labelKey: 'nav.dashboard',
                to: '/dashboard',
                testId: 'tab-dashboard',
                condition:
                    isTabEnabledByRole(locationId, companyId, role, 'dashboard', salesDashboardOnly) &&
                    Boolean(appConfig.dashboardShowI2XMetrics)
            },
            {
                value: PAGES.CALL_HISTORY_NEW,
                labelKey: 'nav.callHistory',
                to: '/call-history',
                testId: 'tab-call-history',
                condition:
                    isTabEnabledByRole(locationId, companyId, role, 'callHistory', salesDashboardOnly) &&
                    Boolean(appConfig.callHistory)
            },
            {
                value: PAGES.REPORTS,
                labelKey: 'nav.reports',
                to: '/reports',
                testId: 'tab-reports',
                condition:
                    isTabEnabledByRole(locationId, companyId, role, 'reports', salesDashboardOnly) &&
                    Boolean(appConfig.reports)
            },
            {
                value: PAGES.KEYWORD_MANAGER,
                labelKey: 'nav.keywordManager',
                to: '/keyword-manager',
                testId: 'tab-keyword-manager',
                condition:
                    isTabEnabledByRole(locationId, companyId, role, 'keywordManager', salesDashboardOnly) &&
                    Boolean(appConfig.adminKeywordManager)
            },
            {
                value: PAGES.USER_MANAGER,
                labelKey: 'nav.userManagement',
                to: '/user-management',
                testId: 'tab-user-manager',
                condition:
                    isTabEnabledByRole(locationId, companyId, role, 'userManager', salesDashboardOnly) &&
                    Boolean(appConfig.adminUserManager)
            },
            {
                value: PAGES.SALES,
                labelKey: 'nav.sales',
                to: '/sales',
                testId: 'tab-sales',
                condition: Boolean(appConfig.salesTab)
            },
            {
                value: PAGES.KPI_MANAGER,
                labelKey: 'nav.kpiManager',
                to: '/kpi-manager',
                testId: 'tab-kpi-manager',
                condition: Boolean(appConfig.kpiManager)
            },
            {
                value: PAGES.QUALITY_MANAGEMENT,
                labelKey: 'nav.qualityAssurance',
                to: '/quality-management',
                testId: 'tab-quality-management',
                condition: Boolean(appConfig.qualityManagement)
            }
        ],
        [locationId, companyId, role, salesDashboardOnly, appConfig]
    );

    const visibleTabs = useMemo(() => tabs.filter(tab => tab.condition), [tabs]);

    return (
        <AppBar>
            <Toolbar classes={{ root: classes.toolbar }}>
                <Box className={classes.logoContainer}>
                    <Logo color={theme.palette.headerLogoColor} />
                    <Box className={classes.badges}>
                        <Box className={classes.germanFlag}>
                            <div className={classes.germanStripeBlack} />
                            <div className={classes.germanStripeRed} />
                            <div className={classes.germanStripeGold} />
                        </Box>
                        <Box className={classes.euFlag}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 512 512">
                                <path d="m256 46.305-9.404 19.054-21.03 3.056 15.217 14.832-3.592 20.945L256 94.305l18.81 9.888-3.593-20.945 15.217-14.832-21.03-3.057L256 46.304zM167.566 72.63l-9.404 19.056-21.03 3.056 15.218 14.832-3.592 20.946 18.808-9.89 18.81 9.89-3.593-20.946L198 94.742l-21.03-3.056-9.404-19.055zm176.868 0-9.405 19.056L314 94.742l15.217 14.832-3.592 20.946 18.81-9.89 18.807 9.89-3.592-20.946 15.217-14.832-21.03-3.056-9.403-19.055zm-243.868 67.425-9.404 19.054-21.03 3.056 15.218 14.832-3.592 20.945 18.808-9.888 18.81 9.888-3.593-20.945L131 162.166l-21.03-3.057-9.404-19.055zm310.868 0-9.405 19.054-21.03 3.056 15.217 14.832-3.592 20.945 18.81-9.888 18.807 9.888-3.592-20.945 15.217-14.832-21.03-3.057-9.403-19.055zM76.566 228.55l-9.404 19.054-21.03 3.056 15.218 14.832-3.592 20.945 18.808-9.888 18.81 9.887-3.593-20.945L107 250.66l-21.03-3.056-9.404-19.055zm358.868 0-9.405 19.054L405 250.66l15.217 14.832-3.592 20.945 18.81-9.888 18.807 9.887-3.592-20.945 15.217-14.832-21.03-3.056-9.403-19.055zm-334.868 89.897-9.404 19.055-21.03 3.057 15.218 14.83-3.592 20.946 18.808-9.89 18.81 9.89-3.593-20.945L131 340.56l-21.03-3.058zm310.868 0-9.405 19.055L381 340.56l15.217 14.83-3.592 20.946 18.81-9.89 18.807 9.89-3.592-20.945 15.217-14.83-21.03-3.058-9.403-19.055zm-243.868 65.746-9.404 19.055-21.03 3.057 15.218 14.832-3.592 20.945 18.808-9.89 18.81 9.89-3.593-20.945L198 406.305l-21.03-3.057zm176.868 0-9.405 19.055-21.03 3.057 15.217 14.832-3.592 20.945 18.81-9.89 18.807 9.89-3.592-20.945 15.217-14.832-21.03-3.057-9.403-19.055zm-88.61 23.614-9.404 19.056-21.03 3.055 15.217 14.834-3.59 20.943.385-.203-.035.203L256 455.898l18.633 9.797-.035-.203.386.203-3.59-20.943 15.215-14.834-21.03-3.055-9.404-19.056-.176.355-.176-.355z" />
                            </svg>
                        </Box>
                        {isChristmasThemeEnabled && (
                            <Box
                                component="img"
                                src="/assets/christmas-tree.png"
                                alt="Christmas tree"
                                className={classes.christmasMascot}
                            />
                        )}
                        {easterTheme.enabled && (
                            <Box
                                component="img"
                                src="/assets/easter/banny.gif"
                                alt="Easter bunny"
                                className={classes.seasonalMascot}
                                sx={{ filter: easterTheme.badgeGlow }}
                            />
                        )}
                        {/*<Box className={classes.germanText}>Made & Hosted in Germany</Box>*/}
                    </Box>
                </Box>
                <Tabs
                    className={classes.tabs}
                    value={page}
                    variant="scrollable"
                    scrollButtons="auto"
                    textColor="inherit"
                >
                    {visibleTabs.map(tab => (
                        <Tab
                            key={tab.value}
                            component={Link}
                            classes={{ root: classes.linkTabRoot, selected: classes.linkTabSelected }}
                            value={tab.value}
                            label={localization.getText(tab.labelKey)}
                            to={tab.to}
                            data-test={tab.testId}
                            sx={
                                easterTheme.enabled && tab.value === page
                                    ? {
                                          textShadow: `0 0 14px ${easterTheme.headerIndicatorColor}33`
                                      }
                                    : undefined
                            }
                        />
                    ))}
                </Tabs>

                <Box className={classes.rightBox}>
                    {shouldShowWhatsNew && <WhatsNew />}
                    {appConfig.languageSelectorEnabled && <GlobalLanguageSelector />}
                    <UserName name={userName} />
                    <Fab className={classes.logoutButton} onClick={logout} data-test="logout-button">
                        <PowerSettingsNewIcon />
                    </Fab>
                </Box>
            </Toolbar>
        </AppBar>
    );
};
